import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, Input, model, OnChanges, signal, SimpleChanges, untracked} from '@angular/core';
import {RelativeDateFilterRangeDto, RelativeDateOrTimeOperator, RelativeDateOrTimeValuesDto, RelativeDateTimeFilterUnit, VisualService} from '../../../services/api.services';
import {Observable, of, ReplaySubject, Subject} from 'rxjs';
import {finalize, map, startWith, switchMap, tap} from 'rxjs/operators';
import {LocalisationService} from '../../../services/localisation.service';
import {LocalDatePipe} from '../../pipes/local-date.pipe';
import { catchAppError, isNullOrWhitespace } from '../../utils';
import { debounced } from 'src/app/pbir/utils/debounced';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-relative-date-range[filter]',
  templateUrl: './relative-date-range.component.html',
  styleUrls: ['./relative-date-range.component.scss'],
  providers: [LocalDatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RelativeDateRangeComponent  {
  readonly destroyRef = inject(DestroyRef);
  readonly filter = input<RelativeDateOrTimeValuesDto>();
  readonly stylingInfo = input<any>({});
  readonly billingCenter = input<string>(null);
  readonly loading = model(false);
  readonly rangeText = signal<string>(null);

  defaultStylingInfo = {
    'font-size': '12px',
    'padding-top': '0.25rem'
  };

  constructor(
    private visualService: VisualService,
    public localisationService: LocalisationService,
    private datePipe: LocalDatePipe
  ) {}

  readonly mergedStylingInfo = computed(() => {
    return { ...this.defaultStylingInfo, ...this.stylingInfo };
  });

  readonly getDisplayDatesEffect = effect(() => {
    if (this.filter()) {
      this.billingCenter();
      untracked(() => this.getDisplayDatesDebounced());
    }
  });

  getDisplayDatesDebounced = debounced(() => {
    this.getDisplayDates();
  }, 400, this.destroyRef);

  getDisplayDates() {
    if ((this.filter().unitType == RelativeDateTimeFilterUnit.SpendPeriod
      || this.filter().unit2Type == RelativeDateTimeFilterUnit.SpendPeriod)
      && isNullOrWhitespace(this.billingCenter())) {
      return null;
    }

    this.loading.set(true);

    this.visualService.getRelativeDateRange(this.billingCenter(), this.filter()).pipe(
      tap((range) => {
        this.rangeText.set(this.formatDisplay(range));
      }),
      catchAppError({ showErrorSnack: false }),
      finalize(() => this.loading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private formatDisplay(range: RelativeDateFilterRangeDto): string {
    const { startDate, endDate } = range;

    const isStartPeriod = this.filter().unitType == RelativeDateTimeFilterUnit.SpendPeriod;
    let isEndPeriod = isStartPeriod;

    if (this.filter().operator == RelativeDateOrTimeOperator.InRange
      || this.filter().operator == RelativeDateOrTimeOperator.OutsideRange) {
      isEndPeriod = this.filter().unit2Type == RelativeDateTimeFilterUnit.SpendPeriod;
    }

    const startDateStr = this.formatDateString(startDate, isStartPeriod);
    const endDateStr = this.formatDateString(endDate, isEndPeriod);

    if (!startDateStr && !endDateStr) return 'All dates';
    if (!startDateStr) return `Prior to and including ${endDateStr}`;
    if (startDateStr && !endDateStr) return `After and including ${startDateStr}`;
    if (startDateStr == endDateStr) return startDateStr;

    return `${startDateStr} - ${endDateStr}`;
  }

  private formatDateString(date: string, isPeriod: boolean): string {
    const format = isPeriod ? "yyyy/MM" : this.localisationService.CUSTOM_DATE_FORMATS.mediumDate;
    return date ? this.datePipe.transform(date, format) : null
  }
}
